import React, { useRef } from 'react';
import { Link } from 'react-router';
import { Points } from 'components/Points/Points';
import { FoilCardImage } from 'components/FoilCardImage';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { AssetUrls } from 'config/assets';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import { TradeAlert } from 'store/services/tradeAlerts/tradeAlertsApi';
import './AlertCard.css';

// Inline SVG card-back placeholder — never depends on S3/CDN, can't 403
const CARD_BACK_DATA_URI = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='223' height='311' viewBox='0 0 223 311'%3E%3Crect width='223' height='311' rx='12' fill='%231a1a2e'/%3E%3Crect x='8' y='8' width='207' height='295' rx='8' fill='none' stroke='%236F51E4' stroke-width='2' opacity='0.4'/%3E%3Ctext x='111.5' y='165' text-anchor='middle' fill='%236F51E4' font-size='48' opacity='0.5'%3E%E2%9C%A6%3C/text%3E%3C/svg%3E";

// Build the ordered fallback chain (CDN size variations + scryfall + inline back).
const buildFallbacks = (alert: TradeAlert): string[] => {
  const fallbacks: string[] = [];
  const url = alert.card.imageUrl;
  if (url) {
    if (url.includes('/normal/')) {
      fallbacks.push(url.replace('/normal/', '/small/'));
      fallbacks.push(url.replace('/normal/', '/large/'));
    } else if (url.includes('/large/')) {
      fallbacks.push(url.replace('/large/', '/normal/'));
      fallbacks.push(url.replace('/large/', '/small/'));
    } else if (url.includes('/small/')) {
      fallbacks.push(url.replace('/small/', '/normal/'));
    }
  }
  if (alert.card.scryfallId) {
    fallbacks.push(AssetUrls.cards.getCardImage(alert.card.scryfallId, 'normal'));
    fallbacks.push(AssetUrls.cards.getCardImage(alert.card.scryfallId, 'small'));
  }
  return fallbacks;
};

interface AlertCardProps {
  alert: TradeAlert;
  /** Which tab the card is being shown in — controls the dismiss vs restore icon. */
  variant: 'all' | 'hidden';
  onSend: (alert: TradeAlert) => void;
  onHide: (alert: TradeAlert) => void;
  onUnhide: (alert: TradeAlert) => void;
}

export const AlertCard: React.FC<AlertCardProps> = ({ alert, variant, onSend, onHide, onUnhide }) => {
  // Track the fallback step for this card's single image. useRef, NOT dataset —
  // React re-renders recreate the DOM element and would reset dataset values.
  const fallbackStepRef = useRef<number>(0);
  const fallbacksRef = useRef<string[]>(buildFallbacks(alert));

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const step = fallbackStepRef.current;
    fallbackStepRef.current = step + 1;
    const fallbacks = fallbacksRef.current;
    if (step < fallbacks.length) {
      e.currentTarget.src = fallbacks[step];
      return;
    }
    e.currentTarget.src = CARD_BACK_DATA_URI;
  };

  const { card, requester } = alert;
  const metaParts = [card.setCode?.toUpperCase(), card.collectorNumber && `#${card.collectorNumber}`]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="alert-card">
      {/* Dismiss / restore */}
      {variant === 'all' ? (
        <button
          className="alert-card-dismiss"
          aria-label="Hide alert"
          title="Hide alert"
          onClick={() => onHide(alert)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
        <button
          className="alert-card-dismiss"
          aria-label="Restore alert"
          title="Restore alert"
          onClick={() => onUnhide(alert)}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 14L4 9l5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 9h11a5 5 0 0 1 0 10h-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Card image (5/7 aspect ratio) */}
      <div className="alert-card-image">
        <FoilCardImage
          src={card.imageUrl || CARD_BACK_DATA_URI}
          alt={card.name}
          foil={card.foil}
          onError={handleImageError}
          loading="lazy"
          borderRadius="8px"
        />
      </div>

      {/* Card name + metadata */}
      <div className="alert-card-name" title={card.name}>{card.name}</div>
      <div className="alert-card-meta">
        {metaParts}
        {card.foil && <span className="alert-card-foil"> · Foil</span>}
      </div>

      {/* Requester */}
      <Link className="alert-card-requester" to={`/profile/u/${requester.username}`}>
        <AvatarWithFlag avatarUrl={requester.avatarUrl} username={requester.username} size={22} />
        <span className="alert-card-requester-name">{requester.username}</span>
      </Link>

      {/* Value + timestamp */}
      <div className="alert-card-value-row">
        <Points points={alert.pointValue} type="small" />
        <span className="alert-card-time">{formatRelativeTime(alert.availableSince)}</span>
      </div>

      {/* Send */}
      <button className="alert-card-send" onClick={() => onSend(alert)}>
        Send
      </button>
    </div>
  );
};

export default AlertCard;
